import { AuditPayload } from "../ts/Ge/types";
import { ATTEMPTS_TO_SEND_AUDIT, AUDIT_TTL_MS, SERVER_URL, SW_VERSION } from "../ts/infrastructure/constants";
import { ApmBase } from '@elastic/apm-rum';

type AuditEventStatus = 'pending' | 'sending' | 'failed';

interface AuditEventItem {
  data: AuditPayload;
  status: AuditEventStatus;
  timestamp: number;
  attempts: number;
  lastError?: string;
  lastAttemptTime?: number;
}

export class AuditEventQueue {
    private static QUEUE_KEY = 'auditEventQueue';
    private static SW_VERSION = SW_VERSION; // Version of the service worker, alter to force update.
    private static hasServiceWorker = false;
    public static isInitialized = false;
    private static apm: ApmBase;
    private static isProcessing = false;

    /**
     * Initialize the queue, check for Service Worker support
     */
    public static async initialize(): Promise<boolean> {
        if (this.isInitialized) return this.hasServiceWorker;

        try {
            // Check if service workers are supported
            if ('serviceWorker' in navigator) {
                // Check if we need to update the service worker
                await this.checkForUpdates();

                // Try to register our service worker
                const registration = await navigator.serviceWorker.register('/sw.js', {
                    scope: '/',
                });

                // Wait until the service worker is active
                if (registration.installing) {
                    await new Promise<void>((resolve) => {
                        registration.installing.addEventListener('statechange', (e) => {
                            if ((e.target as ServiceWorker).state === 'activated') {
                                resolve();
                            }
                        });
                    });
                }

                this.hasServiceWorker = true;
                if (__DEV__) console.log('Audit Service Worker registered and active');

                // Store current version
                localStorage.setItem('sw_version', this.SW_VERSION);

                // Set up message handling from service worker
                navigator.serviceWorker.addEventListener('message', (event) => {
                    if (!event.data || !event.data?.type) return;

                    switch (event.data.type) {
                        case 'audit_event_processed':
                            // Handle confirmation from service worker
                            if (__DEV__) console.log('Audit event processed by service worker:', event.data.eventId);
                            break;
                            
                        case 'sw_error':
                            const swError = new Error(event.data.error.message || 'Unknown service worker error');
    
                            // Create a custom object with all the metadata
                            const errorData = {
                                source: 'service_worker',
                                errorType: event.data.error.type || 'unknown',
                                eventId: event.data.error?.eventId,
                                httpStatus: event.data.error?.httpStatus,
                                errorCategory: event.data.error?.errorCategory,
                                willRetry: event.data.error?.willRetry,
                                attempts: event.data.error?.attempts,
                                timestamp: event.data.error?.timestamp || Date.now()
                            };

                            Object.assign(swError, errorData);

                            // Report to APM
                            if (this.apm) {
                                this.apm.captureError(swError);
                            }
    
                            // Acknowledge receipt
                            if (navigator.serviceWorker.controller) {
                                navigator.serviceWorker.controller.postMessage({
                                    type: 'error_received',
                                    errorId: event.data.error.id
                                });
                            }
                            break;
                    }
                });
            }
        } catch (e) {
            this.apm.captureError(e);
            if (__DEV__) console.error('Service Worker registration failed:', e);

            this.hasServiceWorker = false;
        }

        this.isInitialized = true;
        return this.hasServiceWorker;
    }

    /**
     * Add an event to the queue
     */
    public static async enqueue(eventData: AuditPayload): Promise<string> {
        const eventId = `audit-${Date.now()}-${eventData.outlook.event?.trace_id || 'unknown_trace'}`;

        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            if (this.hasServiceWorker) {
                if (navigator.serviceWorker.controller) {
                    // Use MessageChannel for two-way communication
                    const messageChannel = new MessageChannel();
                    
                    // Wait for the service worker to acknowledge receipt
                    const ackPromise = Promise.race([
                        new Promise<void>((resolve) => {
                            messageChannel.port1.onmessage = (_event) => {
                                if (__DEV__) console.log('Service worker acknowledged audit event:', eventId);
                                resolve();
                            };
                        }),
                        // Timeout if no response in 2 seconds so we never wait indefinitely
                        new Promise<void>((resolve) => setTimeout(resolve, 2000))
                    ]);
                    
                    // Send to service worker with MessageChannel port
                    navigator.serviceWorker.controller.postMessage({
                        type: 'enqueue_audit_event',
                        eventId,
                        eventData,
                    }, [messageChannel.port2]);
                    
                    // Wait for acknowledgment
                    await ackPromise;
                    
                    return eventId;
                }
            }

            // Fallback to localStorage
            this.enqueueInLocalStorage(eventId, eventData);

            // Process
            this.scheduleProcessing();

        } catch (e) {
            this.apm.captureError(e);
            console.error('Error enqueueing audit event', e);
            // final fallback
            this.sendEventAndForget(eventData);
        }

        return eventId;
    }

    // Store event in localStorage
    private static enqueueInLocalStorage(eventId: string, eventData: AuditPayload): void {
        const queue = this.getQueue();

        queue[eventId] = {
            data: eventData,
            status: 'pending' as AuditEventStatus,
            timestamp: Date.now(),
            attempts: 0
        };

        localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
    }

    private static scheduleProcessing(): void {
        if (this.isProcessing) return;

        setTimeout(() => {
            this.isProcessing = true;
            this.processQueue().finally(() => {
                this.isProcessing = false;
            });
        }, 0);
    }

    private static async processQueue(): Promise<void> {
        const queue = this.getQueue();
        let storageNeedsUpdate = false;

        // Clean up ALL expired events.
        const now = Date.now();
        for (const id of Object.keys(queue)) {
            const item = queue[id];
            if (now - item.timestamp > AUDIT_TTL_MS) {
                delete queue[id];
                storageNeedsUpdate = true;
            }
        }

        // If we cleaned up any expired events, update storage
        if (storageNeedsUpdate) {
            localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
        }

        const pendingIds = Object.keys(queue).filter(id => queue[id].status === 'pending');
        if (pendingIds.length === 0) return;

        // Process one by one to avoid overwhelming the browser
        for (const id of pendingIds) {
            const item = queue[id];

            item.status = 'sending';
            item.attempts++;
            localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));

            const success = await this.sendEventAndForget(item.data);

            if (success) {
                delete queue[id];
            } else if (item.attempts >= ATTEMPTS_TO_SEND_AUDIT) {
                item.status = 'failed';
                this.reportError(new Error(`Failed to send audit event after ${ATTEMPTS_TO_SEND_AUDIT} attempts: ${id}`));
            } else {
                item.status = 'pending';
            }
        }

        // Update the queue in localStorage after processing.
        localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
    }

    /**
     * Send an event to the server without waiting for a response.
     */
    private static async sendEventAndForget(data: AuditPayload): Promise<boolean> {
        const auditEventUrl = `${SERVER_URL}/api/3.1/organizations/outlook/auditing/`;

        try {
            const cwpTokenData = JSON.parse(localStorage.getItem('cwpToken') || '{}');
            if (!cwpTokenData?.token) return false;

            const headers = {
                'Content-Type': 'application/json; charset=utf-8',
                'Authorization': `JWTBEARER ${cwpTokenData.token}`,
                'NCC-DEVICE-IDENTIFIER': 'outlook_pep_addin',
            };

            // TODO sendBeacon first - NO authorization header support, backend needs to support it.

            fetch(auditEventUrl, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(data),
                keepalive: true
            }).catch((e) => {
                this.reportError(e);
            });

            return true;
        } catch (e) {
            this.reportError(e);
            if (__DEV__) console.error('Failed to send audit event:', e);

            return false;
        }
    }

    private static getQueue(): Record<string, AuditEventItem> {
        try {
            const stored = localStorage.getItem(this.QUEUE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (e) {
            this.reportError(e);
            console.error('Error reading audit queue', e);

            return {};
        }
    }

    /**
     * Check for any pending events from previous sessions and process them
     */
    public static async checkAndSendPendingEvents(): Promise<void> {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (!this.hasServiceWorker) {
            this.scheduleProcessing();

            this.apm?.addLabels({ 'audit_event_queue': 'No service worker for auditing, falling back to localstorage' });
        } else if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'process_pending_events'
            });

            this.apm?.addLabels({ 'audit_event_queue': 'Service Worker'});
        }
    }

    static setApm(apm: ApmBase) {
        this.apm = apm;
    }

    static reportError(error: Error | string): void {
        if (this.apm) {
            this.apm.captureError(error);
        }

        if (__DEV__) console.error('AuditEventQueue Error:', error);
    }

    /**
     * Unregister the service worker and clear related storage NOT USED now
     */
    public static async unregisterServiceWorker(): Promise<boolean> {
        if (!('serviceWorker' in navigator)) return false;

        try {
            const registrations = await navigator.serviceWorker.getRegistrations();

            for (const registration of registrations) {
                const success = await registration.unregister();
                console.log('Service Worker unregistration:', success ? 'successful' : 'failed');
            }

            // Clear related storage TODODO
            localStorage.removeItem('sw_version');

            // We keep IndexedDB data, if schema changes we will have to
            // handle/update properly keeping data or clear/reset data, probably low value for now.

            return true;
        } catch (e) {
            this.reportError(e);
            console.error('Failed to unregister service workers', e);
            
            return false;
        }
    }

    /**
     * Check and update service worker if needed
     */
    private static async checkForUpdates(): Promise<void> {
        const currentVersion = localStorage.getItem('sw_version');

        if (currentVersion !== this.SW_VERSION) {
            if (__DEV__) console.log(`Service worker version mismatch: ${currentVersion} vs ${this.SW_VERSION}, upgrading to latest.`);
            // Version mismatch, unregister old service worker
            await this.unregisterServiceWorker();
        }
        // TODO we go with the sw_version mechanism or we periodically check for updates?
        // Performance wise better to use versioning, but if we want to check for updates
        // we can do it like this and a shouldUpdate timer:

        // else if ('serviceWorker' in navigator) {
        //     // Even if version matches, check for updates
        //     const registrations = await navigator.serviceWorker.getRegistrations();

        //     for (const registration of registrations) {
        //         // Force update check
        //         await registration.update();
        //     }
        // }
    }
}